import type { ReportProjection } from '../services/reportsApi';
import { EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP } from '../domain/externalReview/externalReportLanguage';

export type ReportSourceContextInput = {
  source_context?: string | null;
  source_type?: string | null;
  non_production_mode?: boolean | null;
  production_claim_allowed?: boolean | null;
  real_world_outcome_claimed?: boolean | null;
  risk_reduction_claim_allowed?: boolean | null;
};

export type SafetyReportOutcomeContext = {
  isDemoOrLoopedSource: boolean;
  outcomeMeasurementEvaluable: boolean;
  workflowCompleted: boolean;
  exposureReductionEvaluable: boolean;
  sourceContextLabel: string;
  workflowStatusLabel: string;
  workflowStatusDetail: string;
  outcomeStatusLabel: string;
  exposureReductionLabel: string;
  productionMeasurementLabel: string;
  demoBannerText: string;
  decisionImpactMainText: string;
  decisionImpactSupportingText: string;
  effectivenessVerificationText: string;
  missingInputReplacement: string | null;
  timelineIllustrationLabel: string;
  timelineStatusLabel: string;
  timelineInterpretationNote: string;
  buyerDemoParagraph: string;
  /** @deprecated Use workflowStatusLabel */
  primaryImpactLabel: string;
  /** @deprecated Use workflowStatusDetail */
  secondaryImpactLabel: string;
  /** @deprecated Use workflowCompleted */
  workflowImpactDemonstrated: boolean;
};

export const DEMO_SOURCE_BANNER_TEXT =
  'Demo source context: This report uses looped demo footage. The demo demonstrates the Safety Case workflow, Safety Scan packaging, corrective-action logging, and Signed Safety Scan export. Exposure reduction over time is measured on live site data, not looped footage. Danger-zone observations in this demo are cooldown-shaped; the product target is zone-clear closure plus reopen suppression.';

export const DEMO_WORKFLOW_STATUS_PRIMARY = 'Signed Safety Scan completed';

export const DEMO_WORKFLOW_STATUS_DETAIL =
  'Edgentik converted recurring camera evidence into a Signed Safety Scan package with evidence, corrective-action context, claim boundaries, and exportable handoff files.';

/** @deprecated Use DEMO_WORKFLOW_STATUS_PRIMARY */
export const DEMO_WORKFLOW_IMPACT_PRIMARY = DEMO_WORKFLOW_STATUS_PRIMARY;

export const DEMO_OUTCOME_STATUS_PRIMARY =
  'Exposure reduction not evaluated on this demo source';

export const DEMO_EXPOSURE_REDUCTION_LABEL = DEMO_OUTCOME_STATUS_PRIMARY;

export const DEMO_EXPOSURE_REDUCTION_DETAIL =
  'Looped footage cannot show behavioral change after corrective action. On live site data, Edgentik supports matched follow-up measurement.';

export const DEMO_PRODUCTION_MEASUREMENT_LABEL =
  'Production measurement supported on live data';

function normalizeSourceToken(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

export function isDemoOrLoopedSourceContext(sourceContext?: ReportSourceContextInput | null): boolean {
  if (!sourceContext) return false;
  if (sourceContext.non_production_mode === true) return true;
  if (sourceContext.risk_reduction_claim_allowed === false) return true;
  const joined = [
    sourceContext.source_context,
    sourceContext.source_type,
  ].map(normalizeSourceToken).filter(Boolean).join(' ');
  return /demo|looped|loop|test|synthetic|simulated/.test(joined);
}

function buildDemoOutcomeContext(): SafetyReportOutcomeContext {
  return {
    isDemoOrLoopedSource: true,
    outcomeMeasurementEvaluable: false,
    workflowCompleted: true,
    workflowImpactDemonstrated: true,
    exposureReductionEvaluable: false,
    sourceContextLabel: 'Looped demo footage',
    workflowStatusLabel: DEMO_WORKFLOW_STATUS_PRIMARY,
    workflowStatusDetail: DEMO_WORKFLOW_STATUS_DETAIL,
    outcomeStatusLabel: DEMO_OUTCOME_STATUS_PRIMARY,
    primaryImpactLabel: DEMO_WORKFLOW_STATUS_PRIMARY,
    secondaryImpactLabel: DEMO_WORKFLOW_STATUS_DETAIL,
    exposureReductionLabel: DEMO_EXPOSURE_REDUCTION_LABEL,
    productionMeasurementLabel: DEMO_PRODUCTION_MEASUREMENT_LABEL,
    demoBannerText: DEMO_SOURCE_BANNER_TEXT,
    decisionImpactMainText: DEMO_OUTCOME_STATUS_PRIMARY,
    decisionImpactSupportingText: EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP,
    effectivenessVerificationText: EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP,
    missingInputReplacement: 'Live production source required for exposure-reduction measurement.',
    timelineIllustrationLabel:
      'Technical illustration only. This source is looped, so before/after rates are not used to assess corrective-action effectiveness.',
    timelineStatusLabel: 'Workflow demo only',
    timelineInterpretationNote:
      'Technical illustration only. This source is looped, so before/after rates are not used to assess corrective-action effectiveness.',
    buyerDemoParagraph:
      'This demo demonstrates the Safety Case workflow and evidence handoff. Exposure reduction over time is not evaluated because the source footage is looped. On live site data, the same workflow supports matched follow-up measurement after corrective action.',
  };
}

function buildProductionOutcomeContext(): SafetyReportOutcomeContext {
  return {
    isDemoOrLoopedSource: false,
    outcomeMeasurementEvaluable: true,
    workflowCompleted: false,
    workflowImpactDemonstrated: false,
    exposureReductionEvaluable: true,
    sourceContextLabel: 'Production source',
    workflowStatusLabel: '',
    workflowStatusDetail: '',
    outcomeStatusLabel: '',
    primaryImpactLabel: '',
    secondaryImpactLabel: '',
    exposureReductionLabel: '',
    productionMeasurementLabel: '',
    demoBannerText: '',
    decisionImpactMainText: '',
    decisionImpactSupportingText: '',
    effectivenessVerificationText: '',
    missingInputReplacement: null,
    timelineIllustrationLabel: '',
    timelineStatusLabel: '',
    timelineInterpretationNote: '',
    buyerDemoParagraph: '',
  };
}

export function resolveSafetyReportOutcomeContext(input: {
  reportProjection?: Pick<ReportProjection, 'source_context'> | null;
  technicalDetails?: { source_context?: ReportSourceContextInput | null } | null;
}): SafetyReportOutcomeContext {
  const sourceContext = input.reportProjection?.source_context
    ?? input.technicalDetails?.source_context
    ?? null;
  if (isDemoOrLoopedSourceContext(sourceContext)) {
    return buildDemoOutcomeContext();
  }
  return buildProductionOutcomeContext();
}
